import React from 'react';
import { Target, Eye, User, Award, Calendar, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { companyData } from '../data/content';

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.section 
      id="about" 
      className="py-20 bg-gray-50 dark:bg-gray-800/50"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
    >
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-16"
          variants={itemVariants}
        >
          <motion.h2 
            className="text-4xl font-bold text-gray-900 dark:text-white mb-6"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            About Our Company
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-600 dark:text-gray-300"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Discover our journey, mission, and the visionary leadership behind GSoftNet Studios
          </motion.p>
        </motion.div>

        <motion.div 
          className="space-y-20"
          variants={containerVariants}
        >
          {/* Company Story */}
          <motion.div 
            className="grid lg:grid-cols-2 gap-12 items-center"
            variants={itemVariants}
          >
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-coral-500 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Our Story</h3>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg">
                {companyData.story}
              </p>
              
              <div className="flex items-center space-x-4 p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                <MapPin className="w-6 h-6 text-primary-500" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Established</p>
                  <p className="text-gray-600 dark:text-gray-300">July 20, 2019 • Dar es Salaam, Tanzania</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-primary-50 to-coral-50 dark:from-primary-900/30 dark:to-coral-900/30 rounded-2xl p-8 transform rotate-3">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-8 transform -rotate-3 shadow-xl">
                  <div className="grid grid-cols-2 gap-6">
                    {companyData.stats.map((stat, index) => (
                      <div key={index} className="text-center">
                        <div className="text-3xl font-bold text-primary-500 dark:text-primary-400 mb-2">
                          {stat.number}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Mission & Vision */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Mission */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-coral-500 rounded-lg flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Our Mission</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {companyData.mission}
              </p>
            </div>

            {/* Vision */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-coral-500 to-primary-500 rounded-lg flex items-center justify-center">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Our Vision</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {companyData.vision}
              </p>
            </div>
          </div>

          {/* Director Information */}
          <div className="bg-gradient-to-r from-primary-500 to-coral-500 rounded-3xl p-1 shadow-2xl">
            <div className="bg-white dark:bg-gray-900 rounded-3xl p-8 lg:p-12">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Meet Our Director</h3>
                <p className="text-gray-600 dark:text-gray-300">Leading with expertise and vision</p>
              </div>

              <div className="grid lg:grid-cols-3 gap-8 items-center">
                {/* Director Avatar */}
                <div className="text-center lg:text-left">
                  <div className="w-48 h-48 bg-gradient-to-r from-primary-500 to-coral-500 rounded-full p-1 mx-auto lg:mx-0 mb-6">
                    <img 
                      src={companyData.director.image} 
                      alt={companyData.director.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {companyData.director.name}
                    </h4>
                    <p className="text-primary-500 font-semibold">
                      {companyData.director.position}
                    </p>
                  </div>
                </div>

                {/* Director Info */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <Award className="w-6 h-6 text-primary-500" />
                      <h5 className="font-semibold text-gray-900 dark:text-white">Responsibilities</h5>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">
                      {companyData.director.responsibilities}
                    </p>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <Target className="w-6 h-6 text-coral-500" />
                      <h5 className="font-semibold text-gray-900 dark:text-white">Experience & Skills</h5>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">
                      {companyData.director.experience}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default About;